const features = [
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
    title: 'AI Lawn Analysis',
    description:
      'Snap a photo of your lawn and get instant AI-powered diagnosis. Identify problems, diseases, and get personalized treatment recommendations.',
    color: 'grass',
  },
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>
    ),
    title: 'Custom Care Plans',
    description:
      'Get a personalized lawn care schedule based on your grass type, climate zone, and soil conditions. Never guess what to do next.',
    color: 'sunset',
  },
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
        />
      </svg>
    ),
    title: 'Weather-Smart Reminders',
    description:
      'Automatic notifications that adapt to weather forecasts. Know when to water, when to skip, and when conditions are perfect for treatments.',
    color: 'sky',
  },
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
    ),
    title: 'Progress Tracking',
    description:
      'Watch your lawn transform over time with before/after photos and health scores. Celebrate your improvements and stay motivated.',
    color: 'earth',
  },
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
        />
      </svg>
    ),
    title: 'Product Recommendations',
    description:
      'Get AI-matched fertilizers, seeds, and treatments for your specific lawn. No more guessing at the hardware store.',
    color: 'grass',
  },
  {
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    ),
    title: 'Expert AI Chat',
    description:
      'Ask questions anytime and get instant answers from our lawn care AI. Like having a master gardener in your pocket.',
    color: 'sunset',
  },
];

const colorClasses = {
  grass: {
    bg: 'bg-grass-100',
    text: 'text-grass-600',
    border: 'border-grass-200',
  },
  sunset: {
    bg: 'bg-sunset-100',
    text: 'text-sunset-600',
    border: 'border-sunset-200',
  },
  sky: {
    bg: 'bg-sky-100',
    text: 'text-sky-600',
    border: 'border-sky-200',
  },
  earth: {
    bg: 'bg-earth-100',
    text: 'text-earth-600',
    border: 'border-earth-200',
  },
};

function Features() {
  return (
    <section id="features" className="section-padding bg-gradient-features">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            Your Personal{' '}
            <span className="gradient-text">Lawn Care Expert</span>
          </h2>
          <p className="text-lg text-gray-600">
            Powered by AI that learns your lawn, your climate, and your goals.
            Get the lush, green yard you&apos;ve always wanted.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const colors =
              colorClasses[feature.color as keyof typeof colorClasses];
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl p-6 lg:p-8 shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 ${colors.bg} ${colors.text} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                >
                  {feature.icon}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover accent */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1 ${colors.bg} rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Features;
